import { Button } from '@/src/components/Commons/Button';
import LoaderButton from '@/src/components/Commons/LoaderButton/LoaderButton';
import { useAppStore } from '@/src/store/global';
import { CardanoWallet } from '@/src/types';
import { isEmulator } from '@/src/lib/SmartDB/Commons/Constants/constants';
import { CLOSE } from "@/src/lib/SmartDB/Commons/Constants/images";
import Image from 'next/image';
import * as React from 'react';
import { useEffect, useState } from 'react';
import useWalletActions from '../useWalletActions';
import styles from './SelectWalletModal.module.scss';

interface WalletConnectorProps {
    isMobile?: boolean;
}

export default function SelectWalletModal(props: WalletConnectorProps) {
    //--------------------------------------
    const appStore = useAppStore();
    //--------------------------------------
    const [isOpen, setIsOpen] = React.useState(false);
    const handleOpen = () => {
        setIsOpen(true);
    };
    const handleClose = () => setIsOpen(false);
    //--------------------------------------
    const { isMobile } = props;
    //--------------------------------------
    const [availableWallets, setAvailableWallets] = useState<CardanoWallet[]>([]);
    const [unAvailableWallets, setUnAvailableWallets] = useState<CardanoWallet[]>([]);
    //--------------------------------------
    const [availableWalletsSeeds, setAvailableWalletsSeeds] = useState<Record<string, string>>({});
    const [availableWalletsKeys, setAvailableWalletsKeys] = useState<Record<string, string>>({});
    //--------------------------------------
    const {
        isRefreshing,
        session,
        status,
        walletStore,
        createSignedSession,
        setCreateSignedSession,
        walletConnect,
        walletFromSeedConnect,
        walletFromKeyConnect,
        walletInstall,
        walletSelected,
        setWalletSelected,
    } = useWalletActions();
    //--------------------------------------
    useEffect(() => {
        const available: CardanoWallet[] = [];
        const unAvailable: CardanoWallet[] = [];
        walletStore.cardanoWallets.forEach((wallet) => {
            if (wallet.isInstalled) {
                available.push(wallet);
            } else {
                unAvailable.push(wallet);
            }
        });
        setAvailableWallets(available);
        setUnAvailableWallets(unAvailable);
        setAvailableWalletsSeeds({ 'Master 1': process.env.NEXT_PUBLIC_walletMasterSeed1!, 'Master 2': process.env.NEXT_PUBLIC_walletMasterSeed2! });
    }, [walletStore.cardanoWallets]);
    //--------------------------------------
    useEffect(() => {
        if (walletStore.emulatorDB !== undefined) {
            const privateKeys: Record<string, string> = walletStore.emulatorDB.privateKeys.reduce((acc: Record<string, string>, key: string, index: number) => {
                acc[`Emulator ${index + 1}`] = key;
                return acc;
            }, {});
            setAvailableWalletsKeys(privateKeys);
        }
    }, [walletStore.emulatorDB]);
    //--------------------------------------
    function showWalletButtons() {
        {
            return walletStore.cardanoWallets.map((wallet) => (
                <div
                    key={wallet.wallet}
                    className={styles.wallet}
                    onClick={async () => {
                        if (wallet.isInstalled === true) {
                            await walletConnect(wallet, createSignedSession, true, false, true);
                        } else {
                            await walletInstall(wallet);
                        }
                    }}
                >
                    <div className={styles.walletName}>
                        <Image src={wallet.icon} alt={wallet.name} width={30} height={30}></Image>
                        <p className={styles.text}>
                            {wallet.name} {wallet.isInstalled === false && '[Install]'}
                        </p>
                        {walletStore.isConnecting && walletSelected === wallet.wallet && <LoaderButton />}
                    </div>
                </div>
            ));
        }
    }
    //--------------------------------------
    function showWalletFromSeedButtons() {
        return (
            <>
                {Object.entries(availableWalletsSeeds).map(([name, seed]) => (
                    <div key={'Wallet ' + name + ' - Seed'} className={styles.wallet} onClick={async () => await walletFromSeedConnect(name, seed, createSignedSession, false)}>
                        <div className={styles.walletName}>
                            <p className={styles.text}>{name}</p>
                            {walletStore.isConnecting && walletSelected === name && <LoaderButton />}
                        </div>
                    </div>
                ))}
            </>
        );
    }
    //--------------------------------------
    function showEmulatorWalletButtons() {
        return (
            <>
                {Object.entries(availableWalletsKeys).length > 0 ? (
                    Object.entries(availableWalletsKeys).map(([name, key], index) => (
                        <div
                        key={'Wallet ' + name + ' - Key'}
                            className={styles.wallet}
                            onClick={async () => {
                                await walletFromKeyConnect(name, key,  createSignedSession, false, false);
                            }}
                        >
                            <div className={styles.walletName}>
                                <p className={styles.text}>Wallet Emulator {index + 1}</p>
                                {walletStore.isConnecting && walletSelected === name  && <LoaderButton />}
                            </div>
                        </div>
                    ))
                ) : (
                    <>Loading emulator wallets...</>
                )}
            </>
        );
    }
    const handleClickToggle = () => {
        if (createSignedSession === true) {
            setCreateSignedSession(false);
        } else {
            setCreateSignedSession(true);
        }
    };
    //--------------------------------------
    return (
        <>
            <Button
                mode="connect"
                size="small"
                onClick={() => {
                    if (walletStore.isGettingWalletsDone === true) handleOpen();
                }}
            >
                <>
                    CONNECT WALLET
                    {status === 'loading' || walletStore.isGettingWalletsDone === false || walletStore.isConnecting || walletStore.isLoadingAnyData ? <LoaderButton /> : null}
                </>
            </Button>
            {isOpen === true ? (
                <div className={styles.modal}>
                    <div className={styles.main}>
                        <div className={styles.popUp}>
                            <header className={styles.headerModal}>
                                <h2 className={styles.titleModal}>CONNECT {process.env.NEXT_PUBLIC_CARDANO_NET!} WALLET </h2>
                                <svg width="45" height="45" className={styles.icon} onClick={handleClose}>
                                    <use href={CLOSE}></use>
                                </svg>
                            </header>
                            <div className={styles.walletContainer}>
                                {isEmulator ? (
                                    showEmulatorWalletButtons()
                                ) : (
                                    <>
                                        {showWalletButtons()}
                                        {appStore.siteSettings?.debug === true ? <>{showWalletFromSeedButtons()}</> : null}
                                    </>
                                )}
                            </div>
                            <div className={styles.toggle}>
                                <div className={styles.toggleController}>
                                    <p className={`${styles.text} ${!createSignedSession ? styles.active : ''}`}></p>
                                    <div className={styles.toggle} onClick={handleClickToggle}>
                                        <span className={`${styles.point} ${!createSignedSession ? styles.active : ''}`}></span>
                                    </div>
                                    <p className={`${styles.text} ${createSignedSession ? styles.active : ''}`}>Admin</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}
